import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Upload, FileText, X, Loader2, AlertTriangle, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  uploadFileToStorage,
  MAX_UPLOAD_BYTES,
  FILE_TOO_LARGE_MESSAGE,
} from "@/lib/resumableUpload";

interface JourneyDocumentUploadStepProps {
  stepId: string;
  userId: string;
  documentType: string;
  allowedExtensions: string[];
  instructions?: string;
  isCompleted: boolean;
  existingDocumentUrl?: string;
  onComplete: () => void;
}

export function JourneyDocumentUploadStep({
  stepId,
  userId,
  documentType,
  allowedExtensions,
  instructions,
  isCompleted,
  existingDocumentUrl,
  onComplete,
}: JourneyDocumentUploadStepProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [storageChoice, setStorageChoice] = useState<"upload" | "local">("upload");
  const [localStorageConfirmed, setLocalStorageConfirmed] = useState(false);
  const { toast } = useToast();

  // Only allow PDF uploads
  const pdfOnly = ["pdf"];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    const extension = selectedFile.name.split('.').pop()?.toLowerCase();
    if (!extension || !pdfOnly.includes(extension)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF file only. Photos and images are not accepted.",
        variant: "destructive",
      });
      return;
    }
    if (selectedFile.size > MAX_UPLOAD_BYTES) {
      toast({
        title: "That file is too big",
        description: FILE_TOO_LARGE_MESSAGE,
        variant: "destructive",
      });
      return;
    }
    setFile(selectedFile);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadPercent(0);
    try {
      // Upload to storage. Big scans go up in chunks so a slow connection
      // can resume instead of failing the whole file.
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${documentType}_${Date.now()}.${fileExt}`;

      await uploadFileToStorage({
        bucket: 'journey-documents',
        path: fileName,
        file,
        onProgress: setUploadPercent,
      });

      // Buckets are private — store path as reference
      // Save to user_journey_documents table
      const { error: docError } = await supabase
        .from('user_journey_documents')
        .insert({
          user_id: userId,
          document_type: documentType,
          document_name: file.name,
          file_url: fileName,
          file_path: fileName,
          file_size: file.size,
        });

      if (docError) throw docError;

      // Mark step as completed
      await markStepComplete({ 
        document_url: fileName,
        document_name: file.name,
        storage_method: 'uploaded',
      });

      toast({
        title: "Document uploaded",
        description: "Your Constitution has been securely saved.",
      });

      onComplete();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Your file did not save",
        description:
          error?.message ||
          "Check your connection and try again. Nothing was lost.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadPercent(0);
    }
  };

  const handleLocalStorageConfirm = async () => {
    if (!localStorageConfirmed) return;

    setIsUploading(true);
    try {
      await markStepComplete({ 
        storage_method: 'local',
        confirmed_local_storage: true,
      });

      toast({
        title: "Confirmation recorded",
        description: "You told us your scanned Constitution is kept on your own device.",
      });

      onComplete();
    } catch (error) {
      console.error('Confirmation error:', error);
      toast({
        title: "Error",
        description: "Failed to record confirmation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const markStepComplete = async (actionData: { [key: string]: string | boolean | undefined }) => {
    // Mark step as completed
    const { error: progressError } = await supabase
      .from('user_journey_step_progress')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        verified_completion: true,
        action_data: actionData,
      })
      .eq('user_id', userId)
      .eq('step_id', stepId);

    if (progressError) throw progressError;

    // Unlock next step
    const { data: currentStep } = await supabase
      .from('journey_steps')
      .select('step_number')
      .eq('id', stepId)
      .single();

    if (currentStep) {
      const { data: nextStep } = await supabase
        .from('journey_steps')
        .select('id')
        .eq('step_number', currentStep.step_number + 1)
        .single();

      if (nextStep) {
        await supabase
          .from('user_journey_step_progress')
          .upsert({
            user_id: userId,
            step_id: nextStep.id,
            status: 'active',
            started_at: new Date().toISOString(),
          }, { onConflict: 'user_id,step_id' });
      } else {
        // No more steps - mark journey complete
        await supabase
          .from('user_journey_state')
          .update({
            journey_completed: true,
            journey_completed_at: new Date().toISOString(),
          })
          .eq('user_id', userId);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Instructions - always visible */}
      <div className="bg-muted/50 rounded-lg p-6">
        <h4 className="font-semibold text-center mb-4">Constitution Upload Instructions</h4>
        <ol className="space-y-4">
          {/* Step 1: Read */}
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</span>
            <div>
              <span className="font-medium">Read Your Constitution</span>
              <span className="ml-2 text-xs font-semibold text-destructive uppercase">(Required)</span>
              <p className="text-sm text-muted-foreground mt-1">
                Carefully review your entire Constitution document to understand your rights and responsibilities. This is a critical step that cannot be skipped.
              </p>
            </div>
          </li>
          
          {/* Step 2: Sign & Scan */}
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</span>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Sign & Scan</span>
                <span className="ml-2 text-xs font-semibold text-destructive uppercase">(Required)</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Sign the document in all required signature blocks, then scan it to create a digital PDF copy.
              </p>
              <div className="bg-background/50 rounded-md p-3 space-y-2 text-sm">
                <p className="font-medium text-foreground">Why scan?</p>
                <p className="text-muted-foreground">
                  Your physical Constitution is the original document. A scanned copy serves as a <strong>legally valid backup</strong> in case your physical documents are ever lost or destroyed—replacement copies come at a significant cost. Per your Constitution, a scanned copy can stand as an original.
                </p>
              </div>
              <div className="space-y-1 text-sm">
                <p className="font-medium">Scanning Options:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                  <li>Use a scanner at home, your local library, or FedEx/office supply store</li>
                  <li>Use your phone's Notes app (iPhone) or Google Drive app (Android) to scan as PDF</li>
                </ul>
                <a 
                  href="https://www.youtube.com/results?search_query=how+to+scan+documents+with+phone+as+pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline mt-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  How to scan documents with your phone
                </a>
              </div>
              <div className="flex items-start gap-2 p-2 bg-destructive/10 rounded-md border border-destructive/20">
                <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">
                  <strong>Do not take photos</strong> of your document. Please use a scanner or scanning app to create a proper PDF.
                </p>
              </div>
            </div>
          </li>
          
          {/* Step 3: Upload */}
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">3</span>
            <div className="space-y-2">
              <div>
                <span className="font-medium">Upload Your Scan</span>
                <span className="ml-2 text-xs font-semibold text-muted-foreground uppercase">(Optional)</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Upload your scanned Constitution to create a secure backup in your Wealthy Steward account.
              </p>
              <div className="flex items-start gap-2 p-2 bg-primary/10 rounded-md border border-primary/20">
                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  <strong>Your documents are private and encrypted.</strong> Only you have access — Wealthy Steward staff cannot view your uploaded files.
                </p>
              </div>
            </div>
          </li>
        </ol>
      </div>

      {instructions && (
        <p className="text-muted-foreground text-center">{instructions}</p>
      )}

      {/* Completed State Banner */}
      {isCompleted && (
        <div className="flex items-center justify-center py-6 bg-green-50 rounded-lg border border-green-200">
          <CheckCircle2 className="w-6 h-6 mr-2 text-green-600" />
          <span className="font-medium text-green-700">Document step completed</span>
          {existingDocumentUrl && (
            <Button variant="link" className="ml-2 text-green-700" asChild>
              <a href={existingDocumentUrl} target="_blank" rel="noopener noreferrer">
                View Document
              </a>
            </Button>
          )}
        </div>
      )}

      {/* Update/Upload section - always available */}
      {isCompleted && (
        <p className="text-sm text-muted-foreground text-center">
          Want to upload or update your Constitution? You can still do so below.
        </p>
      )}

      {/* Storage Choice - always visible */}
      <div className="space-y-4">
        <Label className="text-base font-medium">How would you like to store your scanned Constitution?</Label>
        <RadioGroup
          value={storageChoice}
          onValueChange={(value) => {
            setStorageChoice(value as "upload" | "local");
            setLocalStorageConfirmed(false);
          }}
          className="space-y-3"
        >
          <div className="flex items-start space-x-3 p-3 rounded-lg border bg-background hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="upload" id="upload" className="mt-0.5" />
            <div className="space-y-1">
              <Label htmlFor="upload" className="font-medium cursor-pointer">
                Upload my scanned Constitution
                <span className="ml-2 text-xs text-primary font-normal">(Recommended)</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                Securely store your scan in your Wealthy Steward account for redundant backup.
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3 p-3 rounded-lg border bg-background hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="local" id="local" className="mt-0.5" />
            <div className="space-y-1">
              <Label htmlFor="local" className="font-medium cursor-pointer">
                I will store my scan locally
              </Label>
              <p className="text-sm text-muted-foreground">
                Keep your scanned copy on your own device instead of uploading.
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Upload Zone - shown when upload is selected */}
      {storageChoice === "upload" && (
        <>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              isDragging && "border-primary bg-primary/5",
              !isDragging && "border-muted-foreground/25 hover:border-muted-foreground/50"
            )}
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-8 h-8 text-primary" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setFile(null)}
                  className="ml-2"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="w-12 h-12 mx-auto text-muted-foreground" />
                <div>
                  <p className="font-medium">Drag and drop your scanned Constitution here</p>
                  <p className="text-sm text-muted-foreground">
                    or click to browse
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Accepted format: PDF only
                </p>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="max-w-xs mx-auto"
                />
              </div>
            )}
          </div>

          {/* Upload Button */}
          {file && (
            <div className="flex justify-end">
              <Button 
                onClick={handleUpload} 
                disabled={isUploading}
                size="lg"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploadPercent > 0 && uploadPercent < 100
                      ? `Saving, ${uploadPercent}% done`
                      : "Saving your file"}
                  </>
                ) : (

                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {isCompleted ? "Replace Constitution" : "Upload Constitution"}
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Local Storage Confirmation - shown when local is selected */}
      {storageChoice === "local" && (
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg border">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="confirm-local"
              checked={localStorageConfirmed}
              onCheckedChange={(checked) => setLocalStorageConfirmed(checked === true)}
              className="mt-0.5"
            />
            <Label htmlFor="confirm-local" className="text-sm cursor-pointer leading-relaxed">
              I confirm that I have <strong>scanned my signed Constitution</strong> and will store the digital copy securely on my own device.
            </Label>
          </div>
          <div className="flex justify-end">
            <Button 
              onClick={handleLocalStorageConfirm} 
              disabled={!localStorageConfirmed || isUploading}
              size="lg"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Confirming...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {isCompleted ? "Update Confirmation" : "Confirm & Continue"}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Redo Warning */}
      <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          <strong>Important:</strong> Please ensure your document is complete and correctly signed. Document re-creation incurs significant fees.
        </p>
      </div>
    </div>
  );
}
